/**
 * Marketing SSR route manifest.
 *
 * Single source of truth for which public routes are server-rendered. Routes reference their page
 * by id (`page: MarketingPageId`) rather than a component reference — see `marketingPages.ts` for
 * where a page id resolves to a component, and why (route-level code splitting).
 * Imported by:
 *   - frontend `entry-server.tsx` (server bundle) — renders the matched page, resolved eagerly
 *   - frontend `entry-client-marketing.tsx` (browser hydration), via `MarketingShell` — resolves
 *     the same page, synchronously if SSR already registered it, lazily otherwise
 *   - backend `marketingSsrHandler.ts` — exports `marketingRouteSpecs` for path matching
 *
 * Each entry declares which host flavor it applies to: "b2c" (stippl.io,
 * www.stippl.io) or "b2b" (business.stippl.io, b2b.stippl.io, feat-b2b*).
 * The handler resolves host → flavor before matching paths.
 */

// No page-component imports here on purpose — this manifest is imported by the browser entry
// (`entry-client-marketing.tsx`), and importing every page statically is exactly the bundle this
// file used to force on every visitor (business signup, blog widgets, AI page screenshots, all on
// the homepage). Routes reference a page by `page: MarketingPageId`; the actual components live in
// `marketingPages.ts` (client dynamic imports) and are registered eagerly for SSR by
// `entry-server.tsx`. See `marketingPages.ts`'s top comment for the full seam.
import type { MarketingPageId } from "./marketingPages";

import { DMO_CONTENT, HOTELS_CONTENT, INFLUENCERS_CONTENT, TRAVEL_AGENTS_CONTENT } from "./content/businessPageContent";
import {
	AI_TRAVEL_PLANNER_CONTENT,
	BUDGET_PLANNER_CONTENT,
	CHATGPT_TRAVEL_PLANNER_CONTENT,
	CLAUDE_TRAVEL_PLANNER_CONTENT,
	ESIM_CONTENT,
	EXPENSE_TRACKER_CONTENT,
	ITINERARY_PLANNER_CONTENT,
	PACKING_LIST_CONTENT,
	PHOTOBOOK_CONTENT,
	PRO_CONTENT,
	SCRATCH_MAP_CONTENT,
	SOCIAL_TRAVEL_PLATFORM_CONTENT,
	TRAVEL_JOURNAL_CONTENT,
	TRAVEL_REEL_CONTENT,
	TRAVEL_TRACKER_CONTENT,
	YOUR_MAP_CONTENT,
} from "./content/featurePageContent";
import { articleLd, blogLd, breadcrumbLd, collectionPageLd, faqPageLd, howToLd, organizationLd, softwareApplicationLd } from "./seo/jsonLd";
import { buildBlogHreflang, buildBlogListHreflang, buildDiscoverHreflang, buildDiscoverHubHreflang, buildFeatureHreflang, buildHomeHreflang } from "./seo/metaUtils";
import { resolveKbArticleDates } from "./seo/articleDates";
import { discoverImageVariants } from "./seo/discoverImages";
import { HOME_HEAD, hostelHead, isKnownHostel, isKnownRoute, routeHead } from "../partners/madmonkey/routeHead";
import { isKnownPartnerMap, partnerMapHead } from "../partners/maps/headIndex";
import type { PartnerHead } from "../partners/shared/partnerHead";
import { displayCountryName } from "components/b2c/explore/exploreCommon";
import { EXPLORE_LOCALES, LOCALISED_FEATURE_PATHS, LocalisedFeaturePath, MARKETING_LANG_CODES, NonEnglishLocale } from "i18n/urlLocale";
import { FEATURE_PAGE_LOCALE_META, FEATURE_PAGES_WITH_LOCALISED_BODY, FeaturePageLocaleMetaMap } from "./content/featurePageLocaleMeta";

export type HostFlavor = "b2c" | "b2b";

export interface BuildHeadParams {
	hostFlavor: HostFlavor;
	baseUrl: string;
	pathname: string;
	pathParams: Record<string, string>;
	data?: any;
	lang?: string;
	availableLocales?: string[];
}

export interface MarketingHead {
	title: string;
	description: string;
	canonical?: string;
	ogImage?: string;
	ogImageWidth?: number;
	ogImageHeight?: number;
	ogType?: string;
	jsonLd?: object | object[];
	hreflang?: string;
	/** Overrides the default `index, follow` — see CommonHeadOpts.robots. */
	robots?: string;
}

export interface MarketingRoute {
	path: string;
	hostFlavors: HostFlavor[];
	page: MarketingPageId;
	componentProps?: Record<string, any>;
	/**
	 * Keep this route out of sitemap-pages.xml and llms.txt.
	 *
	 * Both select their static paths with `manifest.filter(r => … && !r.path.includes(":"))`
	 * (`buildStipplSitemap` / `buildStipplLlmsTxt` in server.ts), so ANY parameterless route is
	 * published by default. That is right for every marketing page and wrong for any route that
	 * serves `noindex` — the 404s, which exist to be rendered at someone else's URL, and the
	 * generated locale variants of the 14 feature pages (`generateLocalisedFeatureRoutes` below),
	 * which are `noindex, follow` until they graduate — hand-written title/meta AND a fully
	 * translated body, see `content/featurePageLocaleMeta.ts` — because submitting a `noindex`
	 * URL for indexing is the exact contradiction Search Console reports as "submitted URL marked
	 * noindex". Declared here rather than pattern-matched on the path in server.ts: a
	 * `!endsWith("/404")` check is invisible from this file, which is where someone adds the next
	 * route that should not be indexed.
	 */
	excludeFromIndex?: boolean;
	hasLoader?: boolean;
	loaderKey?: "blogList" | "blogArticle" | "discoverGuide" | "discoverHub" | "readyGeos" | "bioLinks" | "creatorPage" | "partnerMap" | "madMonkeyHostel";
	langOverride?: string;
	/**
	 * For a param route over static content (no loader to say so): false when a param names nothing, an unknown
	 * hostel slug. The page then renders the not-found page and the server answers it with a 404.
	 */
	knownParams?: (params: Record<string, string>) => boolean;
	/**
	 * The route needs its loader's data to be worth rendering — a null result means fall
	 * through to the SPA rather than SSR an empty page. True for a blog article whose slug
	 * does not exist, and for a /discover geo that has not passed readiness.
	 */
	requiresData?: boolean;
	buildHead: (params: BuildHeadParams) => MarketingHead;
}

const DEFAULT_OG = "/og-share-image.jpg";

/**
 * Homepage title + meta description, hand-written per language.
 *
 * Never machine-translate these. The primary keyword is a different phrase in each market,
 * not a translation of the English one: NL keeps the English "trip planner" because
 * "reisplanner" is a public-transport query (NS, 9292 own that SERP), and DE uses
 * "Reiseplanung" because "Reiseplaner" measures SD 63 — brand-polluted by Deutsche Bahn,
 * DERTOUR and Marco Polo. Source: stippl-knowledge website/copy-homepage-hubs/homepage-copy.md.
 */
const HOME_META: Record<string, { title: string; description: string }> = {
	en: {
		title: "Free AI Travel Planner | Stippl",
		description:
			"Plan your trip in seconds. Stippl's AI builds a full day-by-day itinerary from one prompt. Free. Hotels, budget, and group planning in one app.",
	},
	nl: {
		title: "Stippl: Gratis AI trip planner",
		description:
			"Plan je reis in seconden. Zeg waar je heen wilt en de AI van Stippl maakt de hele planning. Gratis. Hotels, budget en kosten delen in één app.",
	},
	de: {
		title: "Stippl: Kostenlose KI-Reiseplanung",
		description:
			"Plane deinen Trip in Sekunden. Sag, wohin du willst, und die KI von Stippl baut die ganze Reise. Kostenlos. Hotels, Budget und Kosten teilen in einer App.",
	},
	fr: {
		title: "Stippl: Planificateur de voyage gratuit avec IA",
		description:
			"Planifiez votre voyage en quelques secondes. Dites où vous voulez aller, l'IA de Stippl fait le reste. Gratuit. Hôtels, budget et dépenses partagées.",
	},
	es: {
		title: "Stippl: Planificador de viajes gratis con IA",
		description:
			"Planifica tu viaje en segundos. Dices a dónde quieres ir y la IA de Stippl monta el resto. Gratis. Hoteles, presupuesto y gastos compartidos en una app.",
	},
	it: {
		title: "Stippl: Crea il tuo itinerario di viaggio gratis",
		description:
			"Crea il tuo itinerario di viaggio in pochi secondi. Dici dove vuoi andare e l'IA di Stippl fa il resto. Gratis. Hotel, budget e spese divise in un'app.",
	},
	pt: {
		title: "Stippl: Planejador de viagens grátis com IA",
		description:
			"Planeje sua viagem em segundos. Você diz para onde quer ir e a IA do Stippl monta o resto. Grátis. Hotéis, orçamento e gastos divididos em um app.",
	},
};

function defaultCanonical(baseUrl: string, pathname: string): string {
	return `${baseUrl}${pathname === "/" ? "" : pathname}`;
}

function langCanonical(baseUrl: string, lang: string | undefined, path: string): string {
	const prefix = lang && lang !== "en" ? `/${lang}` : "";
	return `${baseUrl}${prefix}${path === "/" ? "" : path}`;
}

function homeBreadcrumb(baseUrl: string): { name: string; url: string } {
	return { name: "Home", url: baseUrl };
}

function softwareAppCard(baseUrl: string, name: string, description: string): object[] {
	return [softwareApplicationLd({ baseUrl, name, description })];
}

/**
 * Strip inline markdown from text destined for a meta tag.
 *
 * Guide prose comes from the knowledge base's markdown dossiers, and a geo with no `tagline`
 * falls back to `intro[0]` — raw prose. Kyoto's begins "**Kyoto was Japan's capital…**", and
 * those asterisks were being served verbatim in `<meta name="description">`, `og:description`
 * and `twitter:description`, where they would render literally in a search result or a shared
 * link. Caught by reading the HTML Google's Rich Results Test had actually fetched.
 *
 * Deliberately inline-only: bold/italic/code/links, no block constructs. A meta description is
 * one line of plain text, so there is nothing else to preserve.
 */
function stripMarkdown(text: string): string {
	return text
		.replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // [label](url) and images → label
		.replace(/(\*\*|__)(.*?)\1/g, "$2") // bold
		.replace(/(\*|_)(.*?)\1/g, "$2") // italic
		.replace(/`([^`]*)`/g, "$1") // inline code
		.replace(/\s+/g, " ")
		.trim();
}

/**
 * A meta description Google will show whole.
 *
 * Takes complete sentences up to the budget and stops. Never a mid-word cut, because that is
 * what the previous `.slice(0, 300)` did to dossier prose on **96 of 131 /discover pages** —
 * median 211 characters, worst 460, all of them ending mid-sentence in the search result.
 *
 * If even the first sentence is over budget it falls back to a word boundary with an ellipsis,
 * which reads as an intentional trim rather than a broken string. Returns "" when there is
 * nothing usable, so the caller moves to the next candidate rather than emitting a fragment.
 */
function toMetaDescription(text: string, max = 155): string {
	const clean = stripMarkdown(text || "");
	if (!clean) return "";
	if (clean.length <= max) return clean;

	const sentences = clean.split(/(?<=[.!?])\s+/);
	let out = "";
	for (const sentence of sentences) {
		if (out && (out + " " + sentence).length > max) break;
		out = out ? `${out} ${sentence}` : sentence;
		if (out.length > max) break;
	}
	if (out && out.length <= max) return out;

	const cut = clean.slice(0, max - 1);
	const lastSpace = cut.lastIndexOf(" ");
	return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:—–-]+$/, "")}…`;
}

/**
 * Head for a /discover country or destination guide.
 *
 * Only ever reached with a loader result in hand: the route is `requiresData`, and the loader
 * returns null for any geo that has not passed the knowledge base's readiness check. So this
 * never has to describe a thin guide, and the FAQPage below is never built from an empty list.
 *
 * The FAQs it emits are the same ones the page renders in its own `Faq` section — a
 * requirement of the format, not a coincidence worth breaking.
 */
function discoverGuideHead({ baseUrl, data, lang, geo }: { baseUrl: string; data: any; lang?: string; geo: string }): MarketingHead {
	const g = data?.guide || {};
	const name: string = g.name || geo;
	const isCountry = !geo.includes("/");
	const canonical = langCanonical(baseUrl, lang, `/discover/${geo}`);
	// `metaDescription` is written by a person via the knowledge base's `copy/` layer and is the
	// only one of these three that was composed for a search result. The other two are
	// fallbacks: `tagline` is the hero line, and `intro[0]` is dossier prose cut at 300
	// characters — mid-sentence, every time, which is what the copy layer exists to fix.
	const description: string =
		toMetaDescription(g.metaDescription) ||
		toMetaDescription(g.tagline) ||
		toMetaDescription(g.intro?.[0] ? String(g.intro[0]) : "") ||
		`Plan your trip to ${name} with Stippl.`;

	const faqs: { q: string; a: string }[] = Array.isArray(g.faqs) ? g.faqs : [];
	const breadcrumbs = [homeBreadcrumb(baseUrl)];
	if (!isCountry) {
		const countrySlug = geo.split("/")[0];
		breadcrumbs.push({ name: g.country || countrySlug, url: langCanonical(baseUrl, lang, `/discover/${countrySlug}`) });
	}
	breadcrumbs.push({ name, url: canonical });

	// `g.title` is the COMPLETE tag, suffix included — nothing is appended here. The copy
	// layer measures its 60-character limit including " | Stippl" (enforced in the knowledge
	// base's build_guide_documents.py, on the resolved string), and some titles will want no
	// suffix at all. Appending here would silently blow that budget on every one.
	//
	// The fallback is the template `country-hub-spec.md` §3 measures as unwinnable: it is the
	// phrasing encyclopedias use, so it competes with Lonely Planet and the tourist boards by
	// definition. It stays because ~100 geos have no copy file yet, and a geo improves the day
	// someone writes its title rather than the day this ships.
	const title = g.title || `${name} travel guide | Stippl`;
	const heroImage = g.heroPhoto?.url || g.heroPhoto || `${baseUrl}${DEFAULT_OG}`;
	// Discover-grade crops (Unsplash/Pexels only — `[heroImage]` unchanged for anything else,
	// including the `/og-share-image.jpg` fallback above). `imageVariants[0]` is always the 16:9
	// crop, real or not, so it doubles as `og:image` here rather than needing its own fallback.
	const imageVariants = discoverImageVariants(heroImage);
	const variantsGenerated = imageVariants.length > 1;
	const ogImage = imageVariants[0];

	// `dateModified`, from the same `guide.updatedAt` the on-page byline reads
	// (DiscoverCountryPage / DiscoverDestinationPage) — carried onto the guide document by
	// `build_guide_documents.py` off the dossier's own frontmatter (123yb69326q). Before this,
	// the page's JSON-LD carried a `BreadcrumbList` and, where FAQs exist, a `FAQPage` — nothing
	// with a date at all, while `sitemap-pages.xml`'s `<lastmod>` was regenerated correctly on
	// every sync. That gap is exactly what let the two disagree in front of Google with nothing
	// to arbitrate: a hardcoded "August 2026" on the page against a same-day `<lastmod>`.
	//
	// No `datePublished`: nothing upstream of this JSON blob records when a guide FIRST went
	// live, only when it was last touched, and reusing `updatedAt` for both would assert a
	// publish date this pipeline cannot back up. Honest omission beats a plausible-looking guess.
	//
	// The whole `Article` entry is skipped, not just its dates, when `updatedAt` is absent (2
	// dossiers) — a dateless Article adds nothing this page doesn't already have (the
	// `BreadcrumbList`/`FAQPage` below still emit), and "emit nothing" was the explicit ask.
	const dateModified: string | undefined = typeof g.updatedAt === "string" ? g.updatedAt : undefined;

	return {
		title,
		description,
		canonical,
		ogImage,
		// Real dimensions only when a crop was actually generated — the passthrough case (an
		// unrecognised host, or the `/og-share-image.jpg` fallback) has no guaranteed 16:9 shape.
		...(variantsGenerated ? { ogImageWidth: 1600, ogImageHeight: 900 } : {}),
		// Was `website` (the `buildCommonHead` default) — a guide is an article, and Discover
		// treats `og:type` as one of its eligibility signals.
		ogType: "article",
		hreflang: buildDiscoverHreflang(baseUrl, geo),
		jsonLd: [
			breadcrumbLd(breadcrumbs),
			...(dateModified
				? [
						articleLd({
							baseUrl,
							pageUrl: canonical,
							headline: title,
							description,
							images: imageVariants,
							dateModified,
						}),
					]
				: []),
			...(faqs.length
				? [
						faqPageLd(
							faqs.map((f) => ({ question: f.q, answer: f.a })),
							`${name} travel FAQ`
						),
					]
				: []),
		],
	};
}

/**
 * The /discover guide routes, in English and in each locale Explore is translated into.
 *
 * Generated rather than written out: ten near-identical specs differing only in prefix is
 * exactly the shape that drifts when someone edits nine of them. `EXPLORE_LOCALES` bounds the
 * set rather than `MARKETING_LANG_CODES` — the two are equal today (both 7, since the 2026-09-08
 * translation pass filled it/pt), but this must keep reading `EXPLORE_LOCALES`: a future
 * marketing locale added before Explore has content for it would otherwise get a route serving
 * English prose under that locale's URL. See the note above `EXPLORE_LOCALES` in
 * `frontend/src/i18n/urlLocale.ts`.
 */
const DISCOVER_GUIDE_ROUTES: MarketingRoute[] = EXPLORE_LOCALES.flatMap((locale) => {
	const prefix = locale === "en" ? "" : `/${locale}`;
	return [
		{ suffix: "/discover/:country", geoOf: (p: Record<string, string>) => p.country, page: "DiscoverCountryRoute" as const },
		{
			suffix: "/discover/:country/:destination",
			geoOf: (p: Record<string, string>) => `${p.country}/${p.destination}`,
			page: "DiscoverDestinationRoute" as const,
		},
	].map(({ suffix, geoOf, page }) => ({
		path: `${prefix}${suffix}`,
		hostFlavors: ["b2c"] as HostFlavor[],
		page,
		hasLoader: true,
		loaderKey: "discoverGuide" as const,
		requiresData: true,
		...(locale === "en" ? {} : { langOverride: locale }),
		buildHead: ({ baseUrl, pathParams, data, lang }: BuildHeadParams) => discoverGuideHead({ baseUrl, data, lang: lang || locale, geo: geoOf(pathParams) }),
	}));
});

/**
 * Hand-written per-locale copy for `/discover`, the parent hub of every guide above — 123yb6937h0
 * gave it a real SSR'd page for the first time, so there was no existing translated copy to draw
 * from. Functional, not run through the `marketing-copy`/`page-meta`/`hub-naming` skills that
 * country and destination pages get — those are keyword decisions scoped to one geo; this is the
 * index above all of them. Worth a pass from Ilse once the page is live.
 */
interface DiscoverHubLocaleCopy {
	title: string;
	description: string;
	breadcrumbName: string;
}

const DISCOVER_HUB_LOCALE_COPY: Record<string, DiscoverHubLocaleCopy> = {
	en: {
		title: "Explore Destinations — Country & City Guides | Stippl",
		description: "Browse every country and destination guide on Stippl: where to go, what to see, and how to plan the trip once you're there.",
		breadcrumbName: "Discover",
	},
	nl: {
		title: "Ontdek bestemmingen — Land- en stadsgidsen | Stippl",
		description: "Blader door alle land- en bestemmingsgidsen van Stippl: waar je heen kunt, wat je moet zien en hoe je de reis ter plekke plant.",
		breadcrumbName: "Ontdek",
	},
	de: {
		title: "Reiseziele entdecken — Länder- & Stadtführer | Stippl",
		description: "Durchstöbere alle Länder- und Reiseziel-Guides von Stippl: wohin es geht, was du sehen solltest und wie du die Reise vor Ort planst.",
		breadcrumbName: "Entdecken",
	},
	fr: {
		title: "Découvrir les destinations — Guides pays et villes | Stippl",
		description: "Parcourez tous les guides de pays et de destinations de Stippl : où aller, quoi voir et comment organiser votre voyage une fois sur place.",
		breadcrumbName: "Découvrir",
	},
	es: {
		title: "Descubre destinos — Guías de países y ciudades | Stippl",
		description: "Explora todas las guías de países y destinos de Stippl: adónde ir, qué ver y cómo planear el viaje una vez allí.",
		breadcrumbName: "Descubrir",
	},
	it: {
		title: "Scopri le destinazioni — Guide di paesi e città | Stippl",
		description: "Sfoglia tutte le guide di paesi e destinazioni di Stippl: dove andare, cosa vedere e come organizzare il viaggio una volta lì.",
		breadcrumbName: "Scopri",
	},
	pt: {
		title: "Descubra destinos — Guias de países e cidades | Stippl",
		description: "Explore todos os guias de países e destinos da Stippl: para onde ir, o que ver e como planejar a viagem no local.",
		breadcrumbName: "Descobrir",
	},
};

/**
 * Head for `/discover` and its six locale-prefixed siblings.
 *
 * `pathname` always names the route actually being rendered — each of the seven route entries
 * below passes its own matched path through unchanged — so `defaultCanonical(baseUrl, pathname)`
 * alone is enough to get every locale's self-referencing canonical right; there is no `langCanonical`
 * call to make here the way `discoverGuideHead` needs one.
 *
 * `data` is the loader's payload (`loadDiscoverHub`) and may be absent: `buildHead` also runs on
 * the client after a soft navigation, where nothing has fetched yet. The `ItemList` is then
 * simply omitted — an empty list published as structured data is a worse claim than no list.
 */
function discoverHubHead(locale: string, baseUrl: string, pathname: string, data?: any): MarketingHead {
	const copy = DISCOVER_HUB_LOCALE_COPY[locale] || DISCOVER_HUB_LOCALE_COPY.en;
	const canonical = defaultCanonical(baseUrl, pathname);
	const prefix = locale === "en" ? "" : `/${locale}`;
	const countries: { slug: string; name: string }[] = Array.isArray(data?.countries) ? data.countries : [];
	const jsonLd: object[] = [breadcrumbLd([homeBreadcrumb(baseUrl), { name: copy.breadcrumbName, url: canonical }])];
	if (countries.length > 0) {
		jsonLd.push(
			collectionPageLd({
				name: copy.title,
				description: copy.description,
				url: canonical,
				// Country pages only. The destination links on the page are a second level down and
				// listing them here would put ~170 entries in a list whose job is to describe what
				// this page is about; each country's own guide page is where its destinations belong.
				//
				// `displayCountryName` for the same reason `DiscoverHub.tsx` renders it: the stored
				// name is English, and structured data that says "Germany" under a page that shows
				// "Duitsland" describes a different page than the one served.
				items: countries.map((c) => ({ name: displayCountryName(c.name, locale), url: `${baseUrl}${prefix}/discover/${c.slug}` })),
			})
		);
	}
	return {
		title: copy.title,
		description: copy.description,
		canonical,
		ogImage: `${baseUrl}${DEFAULT_OG}`,
		// All seven locale hubs became indexable in the same change that created them — the bare
		// index used to carry `noindex`. Seven near-identical pages with no reciprocal annotation
		// is the shape Google resolves by picking one and discarding the rest, so the cluster is
		// declared from the start rather than left as a follow-up.
		hreflang: buildDiscoverHubHreflang(baseUrl),
		jsonLd,
	};
}

/**
 * `/discover` — the hub above every country/destination guide, and its six locale-prefixed
 * siblings. Written out by hand, one route object per locale, the way the `/blog` list routes
 * are (~line 1130 below) rather than generated the way `DISCOVER_GUIDE_ROUTES`/`HOME_ROUTES`
 * are: `hasLoader: true` routes need an explicit locale-prefixed `path` of their own —
 * `MarketingShell.tsx` does not auto-expand one across `NON_EN_LANGS` — and the blog list is the
 * closer precedent for a loader-backed top-level index page than the guide suffix-generator is.
 */
/**
 * Hand-written per locale, same rule as `DISCOVER_HUB_LOCALE_COPY` above: never machine-translate
 * a title. This one is not a keyword decision at all — it is the tab title someone sees after a
 * link rotted, and today that title is auto-generated from the broken URL's own segments
 * ("not-a-real-country-xyz, discover by de | Stippl").
 */
const NOT_FOUND_LOCALE_COPY: Record<string, { title: string; description: string }> = {
	en: { title: "Page not found | Stippl", description: "That page does not exist. Find travel guides, destination guides and the Stippl trip planner instead." },
	nl: { title: "Pagina niet gevonden | Stippl", description: "Deze pagina bestaat niet. Bekijk in plaats daarvan de reisgidsen, bestemmingen en de Stippl reisplanner." },
	de: { title: "Seite nicht gefunden | Stippl", description: "Diese Seite gibt es nicht. Stattdessen findest du hier Reiseführer, Reiseziele und den Stippl Reiseplaner." },
	fr: { title: "Page introuvable | Stippl", description: "Cette page n'existe pas. Découvrez plutôt les guides de voyage, les destinations et le planificateur Stippl." },
	es: { title: "Página no encontrada | Stippl", description: "Esta página no existe. Explora en su lugar las guías de viaje, los destinos y el planificador de Stippl." },
	it: { title: "Pagina non trovata | Stippl", description: "Questa pagina non esiste. Scopri invece le guide di viaggio, le destinazioni e il pianificatore Stippl." },
	pt: { title: "Página não encontrada | Stippl", description: "Esta página não existe. Veja antes os guias de viagem, os destinos e o planeador da Stippl." },
};

/**
 * `/404` and its six locale siblings — the page `marketingSsrHandler` renders when a marketing URL
 * turns out not to exist (123yb692x98).
 *
 * `noindex, follow`: the page must not be indexed, but its links out to the homepage, /blog and
 * /discover are the point of it, so a crawler that lands on a rotted URL should still follow them.
 * No canonical and no hreflang — there is no content here to be the canonical version OF, and a
 * hreflang cluster across seven 404s would assert that they are translations of one another, which
 * is true and useless.
 *
 * `excludeFromIndex` keeps all seven out of the sitemap and llms.txt; see the field's own comment.
 */
function notFoundHead(locale: string): MarketingHead {
	const copy = NOT_FOUND_LOCALE_COPY[locale] || NOT_FOUND_LOCALE_COPY.en;
	return { title: copy.title, description: copy.description, robots: "noindex, follow" };
}

const NOT_FOUND_ROUTES: MarketingRoute[] = MARKETING_LANG_CODES.map((locale) => ({
	path: locale === "en" ? "/404" : `/${locale}/404`,
	hostFlavors: ["b2c"] as HostFlavor[],
	page: "NotFound" as const,
	excludeFromIndex: true,
	...(locale === "en" ? {} : { langOverride: locale }),
	buildHead: () => notFoundHead(locale),
}));

const DISCOVER_HUB_ROUTES: MarketingRoute[] = [
	{
		path: "/discover",
		hostFlavors: ["b2c"],
		page: "DiscoverHub" as const,
		hasLoader: true,
		loaderKey: "discoverHub",
		requiresData: true,
		buildHead: ({ baseUrl, pathname, data }) => discoverHubHead("en", baseUrl, pathname, data),
	},
	{
		path: "/nl/discover",
		hostFlavors: ["b2c"],
		page: "DiscoverHub" as const,
		hasLoader: true,
		loaderKey: "discoverHub",
		requiresData: true,
		langOverride: "nl",
		buildHead: ({ baseUrl, pathname, data }) => discoverHubHead("nl", baseUrl, pathname, data),
	},
	{
		path: "/de/discover",
		hostFlavors: ["b2c"],
		page: "DiscoverHub" as const,
		hasLoader: true,
		loaderKey: "discoverHub",
		requiresData: true,
		langOverride: "de",
		buildHead: ({ baseUrl, pathname, data }) => discoverHubHead("de", baseUrl, pathname, data),
	},
	{
		path: "/fr/discover",
		hostFlavors: ["b2c"],
		page: "DiscoverHub" as const,
		hasLoader: true,
		loaderKey: "discoverHub",
		requiresData: true,
		langOverride: "fr",
		buildHead: ({ baseUrl, pathname, data }) => discoverHubHead("fr", baseUrl, pathname, data),
	},
	{
		path: "/es/discover",
		hostFlavors: ["b2c"],
		page: "DiscoverHub" as const,
		hasLoader: true,
		loaderKey: "discoverHub",
		requiresData: true,
		langOverride: "es",
		buildHead: ({ baseUrl, pathname, data }) => discoverHubHead("es", baseUrl, pathname, data),
	},
	{
		path: "/it/discover",
		hostFlavors: ["b2c"],
		page: "DiscoverHub" as const,
		hasLoader: true,
		loaderKey: "discoverHub",
		requiresData: true,
		langOverride: "it",
		buildHead: ({ baseUrl, pathname, data }) => discoverHubHead("it", baseUrl, pathname, data),
	},
	{
		path: "/pt/discover",
		hostFlavors: ["b2c"],
		page: "DiscoverHub" as const,
		hasLoader: true,
		loaderKey: "discoverHub",
		requiresData: true,
		langOverride: "pt",
		buildHead: ({ baseUrl, pathname, data }) => discoverHubHead("pt", baseUrl, pathname, data),
	},
];

/**
 * The homepage, in English and in each of the 7 marketing languages.
 *
 * Generated like `DISCOVER_GUIDE_ROUTES` above, but over `MARKETING_LANG_CODES` rather than
 * `EXPLORE_LOCALES`: the homepage has real hand-written copy in all 7 languages (see
 * `home/*.json` under the marketing i18n directory) independently of whether Explore has
 * content in a given locale, so it deliberately does not defer to `EXPLORE_LOCALES` even though
 * the two lists happen to match today. Before this, `/nl`, `/de`, … were not in the manifest at
 * all — `findMarketingRoute`
 * returned null for them, SSR fell through to the SPA shell, and the six non-English homepages
 * shipped with a generic English `<title>` and no server-rendered description or canonical.
 *
 * `buildHead` closes over this `locale` rather than reading `params.lang`: `MARKETING_ROUTE_SPECS`
 * calls every route's `buildHead` with no `lang` to derive `llmsTitle`/`llmsHint` for llms.txt, so
 * a lang-param-dependent implementation would emit the English title into llms.txt for all six
 * non-English routes.
 */
/**
 * The homepage's rating claim, and the only place on the site that emits `aggregateRating`.
 *
 * App Store only, and the copy says so in every language ("4.7 App Store rating", "4,7 Sterne
 * im App Store", …). That qualifier is load-bearing, not decoration: measured 2026-08-25, the
 * App Store is 4.67 across 8,480 ratings in 26 storefronts while Google Play is 3.74 across
 * 2,141, so an unlabelled figure would be a cross-platform claim neither number supports.
 *
 * Replaces two claims that did not survive checking: "4.9 average rating" (no storefront with
 * volume is near it — the only entries above 4.9 are Finland on 16 ratings and Singapore on 3)
 * and "across 180k+ reviews" (the real total across both stores is ~10,600, overstated ~17x).
 *
 * `ratingCount` is the measured figure; the visible copy says "8,000+" so it stays true as the
 * count grows. Both drift — `support/rating-snapshots.md` in stippl-knowledge is refreshed
 * weekly by an n8n workflow, and is the place to re-check before changing these.
 */
const HOME_RATING = { ratingValue: "4.7", ratingCount: 8480 };

const HOME_ROUTES: MarketingRoute[] = MARKETING_LANG_CODES.map((locale) => ({
	path: locale === "en" ? "/" : `/${locale}`,
	hostFlavors: ["b2c"] as HostFlavor[],
	page: "HomeMarketing" as const,
	// Not `requiresData`: the homepage must render whether or not the ready list resolves. It
	// only decides which curated cards are safe to link.
	hasLoader: true,
	loaderKey: "readyGeos" as const,
	...(locale === "en" ? {} : { langOverride: locale }),
	buildHead: ({ baseUrl }: BuildHeadParams) => {
		const meta = HOME_META[locale];
		return {
			title: meta.title,
			description: meta.description,
			canonical: langCanonical(baseUrl, locale, "/"),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			hreflang: buildHomeHreflang(baseUrl),
			jsonLd: [
				organizationLd(baseUrl),
				softwareApplicationLd({
					baseUrl,
					name: "Stippl",
					description:
						"All-in-one travel planning app. Build day-by-day itineraries, manage your travel budget, build packing lists, and share trips with friends.",
					aggregateRating: HOME_RATING,
				}),
			],
		};
	},
}));

/**
 * The bits of the blog-article head that a locale genuinely hand-writes rather than derives.
 *
 * `titleSuffix` is appended to the headline when neither the knowledge base nor Strapi supplies
 * a full meta title. English has always read "$title — Stippl"; the six translated routes have
 * always read "$title — Stippl Blog" — one word longer, and that predates this fix, so it stays
 * rather than being quietly made consistent with English.
 *
 * `fallbackDescription` is the fallback of last resort — what renders when the article itself
 * has no excerpt and no meta description at all. Each line was hand-written for its locale, not
 * machine-translated from the English one, so these must survive verbatim.
 */
interface BlogArticleLocaleCopy {
	titleSuffix: string;
	fallbackDescription: string;
}

const BLOG_ARTICLE_LOCALE_COPY: Record<string, BlogArticleLocaleCopy> = {
	en: { titleSuffix: " — Stippl", fallbackDescription: "Travel planning insights from the Stippl team." },
	nl: { titleSuffix: " — Stippl Blog", fallbackDescription: "Reisplanninginzichten van het Stippl-team." },
	de: { titleSuffix: " — Stippl Blog", fallbackDescription: "Reiseplanungs-Einblicke vom Stippl-Team." },
	fr: { titleSuffix: " — Stippl Blog", fallbackDescription: "Conseils de planification de voyages par l'équipe Stippl." },
	es: { titleSuffix: " — Stippl Blog", fallbackDescription: "Información de planificación de viajes del equipo Stippl." },
	it: { titleSuffix: " — Stippl Blog", fallbackDescription: "Approfondimenti sulla pianificazione dei viaggi dal team Stippl." },
	pt: { titleSuffix: " — Stippl Blog", fallbackDescription: "Insights de planejamento de viagens da equipe Stippl." },
};

/**
 * Head for `/blog/:article` and its six locale-prefixed siblings.
 *
 * Knowledge-base and legacy Strapi articles share every one of these seven URLs while the 412
 * Strapi articles migrate: a knowledge-base article carries its fields flat, a Strapi one nests
 * them under `attributes`. The loader tags which shape it returned (`data.source`), so this
 * normalises off that tag rather than sniffing the shape.
 *
 * This one function is what the six locale routes were missing: each used to hardcode the
 * Strapi shape (`article?.attributes`) with no `source` check at all, so a knowledge-base
 * article requested at `/nl/blog/<slug>` (or de/fr/es/it/pt) normalised to `{}` — headline,
 * description, hero image, dates, author, canonical and JSON-LD all fell back or vanished,
 * while `BlogArticle` rendered a body under that empty head. English was fixed for the
 * knowledge-base migration; the six were not. Routing all seven through one function is what
 * keeps that from happening again — there is only one place left to update per source shape.
 */
function blogArticleHead({ baseUrl, pathname, data, lang }: { baseUrl: string; pathname: string; data: any; lang: string }): MarketingHead {
	const copy = BLOG_ARTICLE_LOCALE_COPY[lang] || BLOG_ARTICLE_LOCALE_COPY.en;
	const blogPath = lang === "en" ? "/blog" : `/${lang}/blog`;

	const isKb = data?.source === "kb";
	// Read the Strapi shape into its own reference before falling back to `{}` — so that
	// fallback (the thing every locale route used to apply unconditionally, KB articles
	// included) is visibly scoped to the `!isKb` branch rather than sitting on the outside.
	const strapiAttrs = data?.article?.attributes;
	const attrs = isKb ? data?.article || {} : strapiAttrs || {};

	const seo = isKb ? { metaTitle: attrs.seoTitle, metaDescription: attrs.metaDescription } : attrs.SEO || {};
	const headline = attrs.title || "Stippl Blog";
	const pageTitle = seo.metaTitle || `${headline}${copy.titleSuffix}`;
	const description = seo.metaDescription || attrs.excerpt || copy.fallbackDescription;
	const heroFormat = isKb ? null : attrs.headerImage?.data?.attributes?.formats?.large || attrs.headerImage?.data?.attributes?.formats?.medium;
	const heroImg = (isKb ? attrs.hero?.url : heroFormat?.url) || `${baseUrl}${DEFAULT_OG}`;
	const heroWidth: number | undefined = heroFormat?.width;
	const heroHeight: number | undefined = heroFormat?.height;
	// 123yb695c3x: for a KB article, `publishedAt || createdAt` / `updatedAt || datePublished`
	// answers "when did this document last get written to", not "when did the article last
	// change" — 1,036 of 1,197 KB articles share one `updatedAt` from a single bulk write. The
	// article's real editorial date is frontmatter `last_updated:` (stored as `lastUpdated`), the
	// same field the visible "Updated <date>" line on the page already reads
	// (`DiscoverArticle.tsx`), and `resolveKbArticleDates` is the one place that turns it into a
	// `datePublished`/`dateModified` pair that can't disagree with what a reader sees on the page.
	// The Strapi shape (`!isKb`) has no `lastUpdated` and keeps its original two lines untouched.
	const { datePublished, dateModified } = isKb
		? resolveKbArticleDates({
				lastUpdated: attrs.lastUpdated,
				publishedAt: attrs.publishedAt,
				createdAt: attrs.createdAt,
				updatedAt: attrs.updatedAt,
			})
		: { datePublished: attrs.publishedAt || attrs.createdAt, dateModified: attrs.updatedAt || attrs.publishedAt || attrs.createdAt };
	const authorName = isKb ? attrs.author?.name : attrs.author?.data?.attributes?.name;
	// The byline's region (`authorByline.ts`'s `Byline.role`, e.g. "Asia & Oceania") — only the
	// KB shape carries it; Strapi's author is a bare `{ name }`. Passed through to `articleLd` as
	// `Person.knowsAbout`, never invented for the legacy shape.
	const authorRegion = isKb ? attrs.author?.role : undefined;
	const url = defaultCanonical(baseUrl, pathname);

	const faqItems = isKb
		? (attrs.faq || []).map((f: any) => ({ question: f.question, answer: f.answer }))
		: (attrs.body_blocks || [])
				.filter((b: any) => b.__component === "blocks.faq")
				.flatMap((b: any) => b.items || [])
				.filter((item: any) => item?.question && item?.answer);

	return {
		title: pageTitle,
		description,
		canonical: url,
		ogImage: heroImg,
		ogImageWidth: heroWidth,
		ogImageHeight: heroHeight,
		ogType: "article",
		// hreflang has to be reciprocal — Google discards the annotations for a cluster whose
		// pages do not all point at each other. All seven routes build it the same way, from the
		// loader's `localeSlugs`, so the set stays reciprocal by construction.
		hreflang: buildBlogHreflang(baseUrl, data?.localeSlugs || {}),
		jsonLd: [
			articleLd({
				baseUrl,
				pageUrl: url,
				headline,
				description,
				// `og:image`/`og:image:width/height` above stay on the single `heroImg` — a social
				// card only ever shows one image. The schema's `image` gets all three Discover
				// crops; passthrough (an unrecognised host) degrades to the same single URL.
				images: discoverImageVariants(heroImg),
				datePublished,
				dateModified,
				authorName,
				authorRegion,
			}),
			breadcrumbLd(
				isKb && attrs.breadcrumbs?.length
					? [
							homeBreadcrumb(baseUrl),
							{ name: "Blog", url: `${baseUrl}${blogPath}` },
							// Skip the article's own crumb: it carries `url: null` and is appended
							// below with the canonical URL, which is the one Google should see.
							...attrs.breadcrumbs.slice(1, -1).map((c: any) => ({ name: c.name, url: `${baseUrl}${c.url}` })),
							{ name: headline, url },
						]
					: [homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}${blogPath}` }, { name: headline, url }]
			),
			...(faqItems.length > 0 ? [faqPageLd(faqItems, headline)] : []),
		],
	};
}

/**
 * `/links` — Stippl's own link-in-bio page, shown in the Instagram and TikTok bios as
 * `links.stippl.io` (a Cloudflare redirect to this path). Spec: stippl-knowledge
 * `work/stippl-links-page.md`.
 *
 * English only, and that is two facts rather than one: `hasLoader: true` is what stops
 * `MarketingShell` fanning the path out to `/nl/links`, `/de/links`, … (it expands only loader-less,
 * `langOverride`-less routes), and `hasLocalisedRoute` is false for it, so the server 301s any
 * locale-prefixed URL back here instead of rendering one.
 *
 * Not for search: `excludeFromIndex` keeps it out of sitemap-pages.xml and llms.txt, `robots` says
 * noindex in the head, and `marketingSsrHandler` sends `X-Robots-Tag: noindex` on the response. No
 * hreflang, no JSON-LD: there is nothing here to describe to a crawler.
 *
 * `hasLoader` without `requiresData`: an empty list is a valid page ("The first route is on its way."),
 * not a reason to fall through to the SPA. The loader (`loadBioLinks`) reads Mongo on every request,
 * deliberately uncached, because a route's `live_from` is its scheduling.
 */
const BIO_LINKS_ROUTE: MarketingRoute = {
	path: "/links",
	hostFlavors: ["b2c"],
	page: "BioLinksPage" as const,
	hasLoader: true,
	loaderKey: "bioLinks",
	excludeFromIndex: true,
	buildHead: ({ baseUrl, pathname }) => ({
		title: "Stippl Links",
		description: "Download the free Stippl app and open the latest routes from our videos.",
		canonical: defaultCanonical(baseUrl, pathname),
		ogImage: `${baseUrl}${DEFAULT_OG}`,
		robots: "noindex, follow",
	}),
};

/**
 * `/c/:username`: a creator's page. Spec: stippl-knowledge `work/creator-page.md`.
 *
 * English only, for the same two reasons as `/links` (`hasLoader` stops `MarketingShell` fanning the path out to
 * locale prefixes, and `hasLocalisedRoute` is false for it, so the server 301s a locale-prefixed URL back here).
 *
 * `requiresData`: a username that is not a creator with a page is not ours to render. The loader returns null and
 * the handler hands the request to the SPA, whose `/:username/:country` route then answers for it. A param route is
 * never in the sitemap or llms.txt (both skip any path with a `:`), and the page is not meant to be there in v1.
 *
 * The head follows the page's `published` flag: `noindex` until the creator has approved it. Without data (the
 * manifest's own check) it is a plain creator head.
 */
const CREATOR_PAGE_ROUTE: MarketingRoute = {
	path: "/c/:username",
	hostFlavors: ["b2c"],
	page: "CreatorPage" as const,
	hasLoader: true,
	loaderKey: "creatorPage",
	requiresData: true,
	buildHead: ({ baseUrl, data, pathParams }) => ({
		title: `${data?.displayName ?? pathParams.username} · Stippl creator`,
		description: data?.tagline || `${data?.displayName ?? pathParams.username} on Stippl: the trips and lists to copy into your own plans.`,
		canonical: `${baseUrl}/c/${encodeURIComponent(data?.username ?? pathParams.username)}`,
		ogImage: data?.heroPhotos?.[0] || `${baseUrl}${DEFAULT_OG}`,
		robots: data?.published === true ? undefined : "noindex, follow",
	}),
};

/** The head of a Mad Monkey page; an unknown id or slug (`head` null) gets the not-found head. */
function partnerHead(head: PartnerHead | null, { baseUrl, pathname }: BuildHeadParams): MarketingHead {
	if (!head) return notFoundHead("en");
	return { title: head.title, description: head.description, canonical: defaultCanonical(baseUrl, pathname), ogImage: head.ogImage, robots: "noindex, nofollow" };
}

/**
 * `/mad-monkey`, `/mad-monkey/routes/:routeId` and `/mad-monkey/hostels/:hostelSlug` — the Stippl x Mad Monkey
 * partner page, a confidential demo until the partner has signed off. Its content is static
 * (`partners/madmonkey/content.ts`) and the page reads its param itself, with one exception: a hostel page's nearby
 * places come from the hostel's synced Stippl list (loader `madMonkeyHostel`, graphql/helpers/partnerMapLoader.ts).
 * No `requiresData`: until that list exists, or when reading it fails, the loader returns null and the page shows its
 * static places, exactly as before the loader. `hasLoader` also stops `MarketingShell` from matching `/<lang>/…`
 * variants, which the server 301s anyway.
 *
 * Out of every index on purpose, three ways like `/links`: `excludeFromIndex` (no sitemap, no llms.txt), `robots`
 * in the head, and `X-Robots-Tag` on the response (`marketingSsrHandler`). Nothing links to it: not the site's
 * navigation, not its footer. English only: the server 301s a locale-prefixed URL back here (`hasLocalisedRoute`
 * is false for it).
 *
 * An unknown route id or hostel slug gets the not-found head here, `knownParams` lets the server answer it with
 * a 404, and the page renders the site's own not-found page.
 */
const MAD_MONKEY_ROUTES: MarketingRoute[] = [
	{
		path: "/mad-monkey",
		hostFlavors: ["b2c"],
		page: "MadMonkeyPage" as const,
		componentProps: { view: "home" },
		excludeFromIndex: true,
		buildHead: (params) => partnerHead(HOME_HEAD, params),
	},
	{
		path: "/mad-monkey/routes/:routeId",
		hostFlavors: ["b2c"],
		page: "MadMonkeyPage" as const,
		componentProps: { view: "route" },
		excludeFromIndex: true,
		knownParams: (params) => isKnownRoute(params.routeId),
		buildHead: (params) => partnerHead(routeHead(params.pathParams.routeId), params),
	},
	{
		path: "/mad-monkey/hostels/:hostelSlug",
		hostFlavors: ["b2c"],
		page: "MadMonkeyPage" as const,
		componentProps: { view: "hostel" },
		excludeFromIndex: true,
		hasLoader: true,
		loaderKey: "madMonkeyHostel",
		knownParams: (params) => isKnownHostel(params.hostelSlug),
		buildHead: (params) => partnerHead(hostelHead(params.pathParams.hostelSlug), params),
	},
];

/**
 * `/p/:partner` — a partner's map page (`partners/maps/<slug>.json`, The Urban Jungle Hostel's first): one route, one
 * page component, every partner a content file. Its head comes from the file (`headIndex.ts` carries the little of it the
 * route table needs: this file is in the entry bundle, the content is not). The Mad Monkey hostels' maps,
 * `/p/mad-monkey-<hostel>`, have no file each: they are made from the hostel data (`partners/madmonkey/hostelMaps.ts`) and
 * `headIndex.ts` makes their heads from the light hostel index. A Mad Monkey hostel that has no map is redirected, before it
 * gets here, to its own page (`partnerMapRedirect.ts`, in the SSR handler).
 *
 * The loader (`partnerMap`, graphql/helpers/partnerMapLoader.ts) reads the partner's synced Stippl list: its places become
 * the highlights and its id the "Save this map" link; for a Mad Monkey hostel's map it also reads the hostel's program (what
 * is on at the hostel). No `requiresData`: a partner without a list yet, or a failed read, gets null and the page renders its
 * content file unchanged.
 *
 * Out of every index the way the Mad Monkey page is: `excludeFromIndex`, `robots` in the head, `X-Robots-Tag` on the
 * response (`marketingSsrHandler`), linked from nowhere on the site. No language prefix: the server 301s a locale-prefixed URL
 * back here, and a page in another language than English is a slug of its own (`/p/indewaag-nl`, its content file's `lang`). A slug that names no partner gets the not-found head, `knownParams` lets the server answer it with a 404,
 * and the page renders the site's own not-found page.
 */
const PARTNER_MAP_ROUTES: MarketingRoute[] = [
	{
		path: "/p/:partner",
		hostFlavors: ["b2c"],
		page: "PartnerMapPage" as const,
		excludeFromIndex: true,
		hasLoader: true,
		loaderKey: "partnerMap",
		knownParams: (params) => isKnownPartnerMap(params.partner),
		buildHead: (params) => partnerHead(partnerMapHead(params.pathParams.partner), params),
	},
];

export const MARKETING_ROUTES: MarketingRoute[] = [
	...NOT_FOUND_ROUTES,
	...DISCOVER_HUB_ROUTES,
	BIO_LINKS_ROUTE,
	CREATOR_PAGE_ROUTE,
	...MAD_MONKEY_ROUTES,
	...PARTNER_MAP_ROUTES,
	...DISCOVER_GUIDE_ROUTES,
	// ------------------------------------------------------------------
	// B2C marketing pages (stippl.io / www.stippl.io)
	// ------------------------------------------------------------------
	...HOME_ROUTES,
	{
		path: "/about",
		hostFlavors: ["b2c"],
		page: "About" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "About Stippl — Our Story & Mission",
			description: "Meet the team behind Stippl — the free travel planning app used by 1.5M+ travelers in 190+ countries. Here's why we started.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [organizationLd(baseUrl), breadcrumbLd([homeBreadcrumb(baseUrl), { name: "About", url: `${baseUrl}/about` }])],
		}),
	},
	{
		path: "/contact",
		hostFlavors: ["b2c"],
		page: "Contact" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Contact Stippl — Help, Partnerships & Press",
			description: "Questions, partnership requests, or press enquiries? Email info@stippl.io — we respond within 48 hours.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [organizationLd(baseUrl), breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Contact", url: `${baseUrl}/contact` }])],
		}),
	},
	// Legal pages. Server-rendered because crawlers that do not run JS (OpenAI's plugin-directory
	// checker among them) must find the policy text in the raw HTML. English only: the pages have no
	// translations, and `hasLocalisedRoute` is false for them, so `/nl/privacy` etc. 301 to here.
	{
		path: "/privacy",
		hostFlavors: ["b2c"],
		page: "PrivacyStatement" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Privacy Policy | Stippl",
			description: "How Stippl B.V. collects, uses and protects your personal data, who receives it, and your rights under the GDPR.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
		}),
	},
	{
		path: "/terms",
		hostFlavors: ["b2c"],
		page: "Terms" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Terms and Conditions | Stippl",
			description: "The terms and conditions for using Stippl: accounts, content, subscriptions, eSIM, Stays bookings and AI features.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
		}),
	},
	{
		path: "/creators",
		hostFlavors: ["b2c"],
		page: "CreatorsPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// The title is load-bearing for the brand sitelink: unchanged from the page this one replaced.
			title: "Stippl for Creators — Grow Audience & Affiliate Revenue",
			description:
				"Turn your travel recommendations into maps and ready-made trips inside Stippl. Earn €0.50 for every active traveller you bring in, put our maps on your website and add your own affiliate links.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}/img/creators/og.jpg`,
			ogImageWidth: 1200,
			ogImageHeight: 630,
			jsonLd: [
				...softwareAppCard(
					baseUrl,
					"Stippl for Travel Creators & Bloggers",
					"Branded itinerary builder and travel content tools for travel creators and bloggers."
				),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "For Creators", url: `${baseUrl}/creators` }]),
			],
		}),
	},
	{
		path: "/influencers",
		hostFlavors: ["b2c"],
		page: "BusinessPage" as const,
		componentProps: INFLUENCERS_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for Influencers — Share Places You Love",
			description:
				"Create branded itineraries for your followers. Share destination tips, grow your following, and monetize your travel recommendations. Free to start.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				...softwareAppCard(baseUrl, "Stippl for Influencers", "Branded itinerary and recommendation sharing tools for travel influencers."),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "For Influencers", url: `${baseUrl}/influencers` }]),
			],
		}),
	},
	{
		path: "/for-hotels",
		hostFlavors: ["b2c"],
		page: "BusinessPage" as const,
		componentProps: HOTELS_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for Hotels — Your digital hotel concierge",
			description: "Enhance guest experience and partnership revenue with a personalised, branded travel app for every guest at your property.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				...softwareAppCard(baseUrl, "Stippl for Hotels", "Digital hotel concierge that delivers branded, personalised travel apps to every guest."),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "For Hotels", url: `${baseUrl}/for-hotels` }]),
			],
		}),
	},
	{
		path: "/for-travel-agents",
		hostFlavors: ["b2c"],
		page: "BusinessPage" as const,
		componentProps: TRAVEL_AGENTS_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for Travel Agents: Win Clients With Better Itineraries",
			description:
				"Turn clients into brand ambassadors. Build beautiful, interactive itineraries in minutes and increase bookings through a better experience.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				...softwareAppCard(
					baseUrl,
					"Stippl for Travel Agents",
					"Visual drag-and-drop itinerary builder for travel agents, branded, collaborative and AI-assisted."
				),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "For Travel Agents", url: `${baseUrl}/for-travel-agents` }]),
			],
		}),
	},
	{
		path: "/for-dmo",
		hostFlavors: ["b2c"],
		page: "BusinessPage" as const,
		componentProps: DMO_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for DMOs — Empower tourists with dynamic destination content",
			description:
				"Promote your destination to active travel planners. Boost discovery of hidden gems and stay present throughout the full journey with Stippl.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				...softwareAppCard(baseUrl, "Stippl for DMOs", "Dynamic destination content and traveller engagement tools for Destination Marketing Organisations."),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "For DMOs", url: `${baseUrl}/for-dmo` }]),
			],
		}),
	},

	// ------------------------------------------------------------------
	// Feature pages (B2C) — target the consumer functional-keyword cluster
	// (trip planner, itinerary planner, AI travel planner, budget planner,
	// packing list, vacation planner, group trip planner, etc.).
	// All render via FeaturePage with content from featurePageContent.ts.
	// ------------------------------------------------------------------
	{
		path: "/itinerary-planner",
		hostFlavors: ["b2c"],
		page: "ItineraryPlannerPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: ITINERARY_PLANNER_CONTENT.seoTitle || ITINERARY_PLANNER_CONTENT.title,
			description: ITINERARY_PLANNER_CONTENT.seoDescription || ITINERARY_PLANNER_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...ITINERARY_PLANNER_CONTENT.softwareApplication }),
				howToLd({ ...ITINERARY_PLANNER_CONTENT.howToUse, steps: ITINERARY_PLANNER_CONTENT.howToSteps }),
				faqPageLd(ITINERARY_PLANNER_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: ITINERARY_PLANNER_CONTENT.pageTitle, url: `${baseUrl}/itinerary-planner` }]),
			],
		}),
	},
	{
		path: "/ai-travel-planner",
		hostFlavors: ["b2c"],
		page: "AiTravelPlannerPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: AI_TRAVEL_PLANNER_CONTENT.seoTitle || AI_TRAVEL_PLANNER_CONTENT.title,
			description: AI_TRAVEL_PLANNER_CONTENT.seoDescription || AI_TRAVEL_PLANNER_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...AI_TRAVEL_PLANNER_CONTENT.softwareApplication }),
				howToLd({ ...AI_TRAVEL_PLANNER_CONTENT.howToUse, steps: AI_TRAVEL_PLANNER_CONTENT.howToSteps }),
				faqPageLd(AI_TRAVEL_PLANNER_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: AI_TRAVEL_PLANNER_CONTENT.pageTitle, url: `${baseUrl}/ai-travel-planner` }]),
			],
		}),
	},
	{
		path: "/chatgpt-travel-planner",
		hostFlavors: ["b2c"],
		page: "AssistantTravelPlannerPage" as const,
		componentProps: { assistant: "chatgpt" },
		buildHead: ({ baseUrl, pathname }) => ({
			title: CHATGPT_TRAVEL_PLANNER_CONTENT.seoTitle,
			description: CHATGPT_TRAVEL_PLANNER_CONTENT.seoDescription,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...CHATGPT_TRAVEL_PLANNER_CONTENT.softwareApplication }),
				howToLd({ ...CHATGPT_TRAVEL_PLANNER_CONTENT.howToUse, steps: CHATGPT_TRAVEL_PLANNER_CONTENT.howToSteps }),
				faqPageLd(CHATGPT_TRAVEL_PLANNER_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: CHATGPT_TRAVEL_PLANNER_CONTENT.pageTitle, url: `${baseUrl}/chatgpt-travel-planner` }]),
			],
		}),
	},
	{
		path: "/claude-travel-planner",
		hostFlavors: ["b2c"],
		page: "AssistantTravelPlannerPage" as const,
		componentProps: { assistant: "claude" },
		buildHead: ({ baseUrl, pathname }) => ({
			title: CLAUDE_TRAVEL_PLANNER_CONTENT.seoTitle,
			description: CLAUDE_TRAVEL_PLANNER_CONTENT.seoDescription,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...CLAUDE_TRAVEL_PLANNER_CONTENT.softwareApplication }),
				howToLd({ ...CLAUDE_TRAVEL_PLANNER_CONTENT.howToUse, steps: CLAUDE_TRAVEL_PLANNER_CONTENT.howToSteps }),
				faqPageLd(CLAUDE_TRAVEL_PLANNER_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: CLAUDE_TRAVEL_PLANNER_CONTENT.pageTitle, url: `${baseUrl}/claude-travel-planner` }]),
			],
		}),
	},
	{
		path: "/budget-planner",
		hostFlavors: ["b2c"],
		page: "BudgetPlannerPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: BUDGET_PLANNER_CONTENT.seoTitle || BUDGET_PLANNER_CONTENT.title,
			description: BUDGET_PLANNER_CONTENT.seoDescription || BUDGET_PLANNER_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...BUDGET_PLANNER_CONTENT.softwareApplication }),
				howToLd({ ...BUDGET_PLANNER_CONTENT.howToUse, steps: BUDGET_PLANNER_CONTENT.howToSteps }),
				faqPageLd(BUDGET_PLANNER_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: BUDGET_PLANNER_CONTENT.pageTitle, url: `${baseUrl}/budget-planner` }]),
			],
		}),
	},
	{
		path: "/expense-tracker",
		hostFlavors: ["b2c"],
		page: "FeaturePage" as const,
		componentProps: EXPENSE_TRACKER_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Travel Expense Tracker — Split Costs Instantly | Stippl",
			description: "Track every expense on the go, split costs with co-travelers, and auto-convert currencies. Free travel expense app for iOS and Android.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({
					baseUrl,
					name: "Stippl Travel Expense Tracker",
					description: "Mobile expense tracking and group settlement tool for travellers.",
				}),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Expense Tracker", url: `${baseUrl}/expense-tracker` }]),
			],
		}),
	},
	{
		path: "/packing-list",
		hostFlavors: ["b2c"],
		page: "PackingListPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: PACKING_LIST_CONTENT.seoTitle || PACKING_LIST_CONTENT.title,
			description: PACKING_LIST_CONTENT.seoDescription || PACKING_LIST_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...PACKING_LIST_CONTENT.softwareApplication }),
				howToLd({ ...PACKING_LIST_CONTENT.howToUse, steps: PACKING_LIST_CONTENT.howToSteps }),
				faqPageLd(PACKING_LIST_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: PACKING_LIST_CONTENT.pageTitle, url: `${baseUrl}/packing-list` }]),
			],
		}),
	},
	{
		path: "/your-map",
		hostFlavors: ["b2c"],
		page: "FeaturePage" as const,
		componentProps: YOUR_MAP_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Personal Travel Map — Pin Places & Build Your Bucket List | Stippl",
			description: "Save the places you love, plan where you want to go next, and share your travel map with friends. Your whole travel life on one free map.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({
					baseUrl,
					name: "Stippl Travel Map",
					description: "Personal travel map for saving and sharing favourite places, recommendations and bucket-list spots.",
				}),
				faqPageLd([
					{
						question: "Is Stippl's travel map free?",
						answer: "Yes. Saving places, building your personal travel map and sharing it with friends is completely free in Stippl on web, iOS and Android.",
					},
					{
						question: "Can I share my travel map with friends?",
						answer:
							"Yes. Share your map directly with friends so they can browse your recommendations for any destination. Your map works as a personal travel guide other people can actually use.",
					},
					{
						question: "Can I save a bucket list on my travel map?",
						answer:
							"Yes. Mark destinations as places you want to visit and they appear as wishlist spots on your map. Your bucket list and your visited places live side by side so you always know what's next.",
					},
					{
						question: "Does the travel map work with my trip itineraries?",
						answer:
							"Yes. Places saved on your map can be added directly to any trip itinerary in Stippl. Discover a great restaurant while browsing your map and drop it into your upcoming trip in one step.",
					},
					{
						question: "Does Stippl's travel map work on mobile?",
						answer:
							"Yes. Your personal travel map is fully accessible on iOS and Android. Browse your saved places, add new spots and share recommendations from your phone.",
					},
				]),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Your Map", url: `${baseUrl}/your-map` }]),
			],
		}),
	},
	{
		path: "/social-travel-platform",
		hostFlavors: ["b2c"],
		page: "FeaturePage" as const,
		componentProps: SOCIAL_TRAVEL_PLATFORM_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Travel Community — Share Trips & Follow Travelers | Stippl",
			description:
				"Connect with friends and like-minded travelers. Follow people you admire, share your adventures, and discover hidden gems recommended by real travelers.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({
					baseUrl,
					name: "Stippl Travel Profile",
					description: "Social platform for travellers to follow friends, share trips and recommend places.",
				}),
				faqPageLd([
					{
						question: "Is Stippl's travel community free?",
						answer:
							"Yes. Creating a profile, following other travellers, sharing trips and discovering recommendations is completely free in Stippl on web, iOS and Android.",
					},
					{
						question: "How do I find other travellers to follow on Stippl?",
						answer:
							"Search for friends by name or username, or discover new travellers through their published trips and shared places. Follow anyone whose recommendations you find interesting.",
					},
					{
						question: "Can I share my trip itinerary with the Stippl community?",
						answer:
							"Yes. Publish any trip itinerary to your Stippl profile and the community can browse it for inspiration. You control which trips are public and which stay private.",
					},
					{
						question: "Is my Stippl profile private?",
						answer:
							"Your profile is public by default so other travellers can discover your recommendations, but you control which trips and journal entries you share. Anything you don't publish stays visible only to you.",
					},
					{
						question: "Can I use Stippl as a travel social network?",
						answer:
							"Yes. Stippl works as a social network built specifically for travel: follow friends, share adventures, discover places through people you trust and build a record of your travel life in one place.",
					},
				]),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Travel Profile", url: `${baseUrl}/social-travel-platform` }]),
			],
		}),
	},
	{
		path: "/travel-journal",
		hostFlavors: ["b2c"],
		page: "FeaturePage" as const,
		componentProps: TRAVEL_JOURNAL_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Travel Journal App — Document & Relive Your Adventures | Stippl",
			description: "Write travel journal entries, add photos, and map your memories — all in one place. Access on mobile and desktop. Free on iOS and Android.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({
					baseUrl,
					name: "Stippl Travel Journal",
					description: "Digital travel journal app for documenting trips with text, photos and shared albums.",
				}),
				faqPageLd([
					{
						question: "Is Stippl's travel journal app free?",
						answer: "Yes. Writing and sharing travel journals in Stippl is completely free on web, iOS and Android.",
					},
					{
						question: "Can I add photos to my travel journal?",
						answer:
							"Yes. Upload photos directly to your journal entries and they appear alongside your written notes. Your journal becomes a combined photo and story record of the trip.",
					},
					{
						question: "Can I keep my travel journal private?",
						answer:
							"Yes. Journals are private by default. You choose when to publish an entry to the Stippl community, unpublished journals are only visible to you.",
					},
					{
						question: "Does Stippl's travel journal work offline?",
						answer: "Yes. Write journal entries offline in the Stippl mobile app and they sync automatically when you're back online.",
					},
					{
						question: "Can I use Stippl as a travel diary app?",
						answer:
							"Yes. Stippl's travel journal feature works as a digital travel diary: write destination-by-destination entries, add photos and build a lasting record of every trip.",
					},
				]),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Travel Journal", url: `${baseUrl}/travel-journal` }]),
			],
		}),
	},
	{
		path: "/travel-tracker",
		hostFlavors: ["b2c"],
		page: "TravelTrackerPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: TRAVEL_TRACKER_CONTENT.seoTitle || TRAVEL_TRACKER_CONTENT.title,
			description: TRAVEL_TRACKER_CONTENT.seoDescription || TRAVEL_TRACKER_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...TRAVEL_TRACKER_CONTENT.softwareApplication }),
				howToLd({ ...TRAVEL_TRACKER_CONTENT.howToUse, steps: TRAVEL_TRACKER_CONTENT.howToSteps }),
				faqPageLd(TRAVEL_TRACKER_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: TRAVEL_TRACKER_CONTENT.pageTitle, url: `${baseUrl}/travel-tracker` }]),
			],
		}),
	},
	{
		path: "/travel-reel",
		hostFlavors: ["b2c"],
		page: "TravelReelPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: TRAVEL_REEL_CONTENT.seoTitle || TRAVEL_REEL_CONTENT.title,
			description: TRAVEL_REEL_CONTENT.seoDescription || TRAVEL_REEL_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...TRAVEL_REEL_CONTENT.softwareApplication }),
				howToLd({ ...TRAVEL_REEL_CONTENT.howToUse, steps: TRAVEL_REEL_CONTENT.howToSteps }),
				faqPageLd(TRAVEL_REEL_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: TRAVEL_REEL_CONTENT.pageTitle, url: `${baseUrl}/travel-reel` }]),
			],
		}),
	},
	{
		path: "/scratch-map",
		hostFlavors: ["b2c"],
		page: "FeaturePage" as const,
		componentProps: SCRATCH_MAP_CONTENT,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Online Scratch Map — Track Countries Visited | Stippl",
			description:
				"Build your personal scratch map of visited countries and bucket-list destinations. Share with friends and show off your travel history. Free on Stippl.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({
					baseUrl,
					name: "Stippl Scratch Map",
					description: "Online scratch map for tracking countries visited and bucket-list destinations.",
				}),
				faqPageLd([
					{
						question: "Is Stippl's scratch map free?",
						answer: "Yes. Creating and sharing your online scratch map is completely free in Stippl on web, iOS and Android.",
					},
					{
						question: "How many countries can I add to my scratch map?",
						answer:
							"You can add all 195 countries in the world to your Stippl scratch map. Mark the ones you've visited, add the ones on your bucket list and watch the map fill up over time.",
					},
					{
						question: "Can I share my scratch map on social media?",
						answer:
							"Yes. Share your scratch map directly to Instagram, Twitter or any other platform. It's an easy way to show off your travel history and inspire other travellers.",
					},
					{
						question: "Does the scratch map work on mobile?",
						answer:
							"Yes. Your Stippl scratch map is fully accessible on iOS and Android. Update it after every trip from your phone and share it with friends on the go.",
					},
					{
						question: "Can I see other people's scratch maps on Stippl?",
						answer:
							"Yes. Scratch maps are visible on Stippl travel profiles. Follow other travellers to see their visited countries and get inspired by where they've been.",
					},
				]),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Scratch Map", url: `${baseUrl}/scratch-map` }]),
			],
		}),
	},
	{
		path: "/esim",
		hostFlavors: ["b2c"],
		page: "EsimPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: ESIM_CONTENT.seoTitle || ESIM_CONTENT.title,
			description: ESIM_CONTENT.seoDescription || ESIM_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...ESIM_CONTENT.softwareApplication }),
				howToLd({ ...ESIM_CONTENT.howToUse, steps: ESIM_CONTENT.howToSteps }),
				faqPageLd(ESIM_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: ESIM_CONTENT.pageTitle, url: `${baseUrl}/esim` }]),
			],
		}),
	},
	{
		path: "/pro",
		hostFlavors: ["b2c"],
		page: "B2CPricingPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: PRO_CONTENT.seoTitle || PRO_CONTENT.title,
			description: PRO_CONTENT.seoDescription || PRO_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...PRO_CONTENT.softwareApplication }),
				faqPageLd(PRO_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: PRO_CONTENT.pageTitle, url: `${baseUrl}/pro` }]),
			],
		}),
	},
	{
		path: "/photobook",
		hostFlavors: ["b2c"],
		page: "PhotobookPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			// seoTitle, not title: the <title> tag is not the H1. Sharing one value is how these
			// pages lost their keyword in the relaunch (123yb692vrt). Falls back so a page without
			// approved SEO copy keeps exactly today's behaviour.
			title: PHOTOBOOK_CONTENT.seoTitle || PHOTOBOOK_CONTENT.title,
			description: PHOTOBOOK_CONTENT.seoDescription || PHOTOBOOK_CONTENT.text,
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				softwareApplicationLd({ baseUrl, ...PHOTOBOOK_CONTENT.softwareApplication }),
				howToLd({ ...PHOTOBOOK_CONTENT.howToUse, steps: PHOTOBOOK_CONTENT.howToSteps }),
				faqPageLd(PHOTOBOOK_CONTENT.faqItems),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: PHOTOBOOK_CONTENT.pageTitle, url: `${baseUrl}/photobook` }]),
			],
		}),
	},

	// ------------------------------------------------------------------
	// B2B marketing pages: under /business prefix on B2C host
	// ------------------------------------------------------------------
	{
		path: "/business",
		hostFlavors: ["b2c"],
		page: "BusinessHomePage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for Business — The visual trip-planning platform for travel agents",
			description:
				"Build beautiful, branded itineraries in minutes with the only drag-and-drop trip planner for travel agents. AI-assisted, fully collaborative, and ready to impress your clients.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				...softwareAppCard(baseUrl, "Stippl for Business", "Visual drag-and-drop trip-planning platform for travel agents and agencies."),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Business", url: `${baseUrl}/business` }]),
			],
		}),
	},
	{
		path: "/business/trip-building",
		hostFlavors: ["b2c"],
		page: "BusinessTripBuildingPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for Business: Drag-and-drop trip builder for travel agents",
			description:
				"Build full itineraries visually: drag and drop days, activities, accommodation, and transport. Real-time collaboration, AI assistance, and branded output.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				...softwareAppCard(baseUrl, "Stippl Trip Builder", "Visual drag-and-drop itinerary builder with real-time collaboration and AI assistance."),
				breadcrumbLd([
					homeBreadcrumb(baseUrl),
					{ name: "Business", url: `${baseUrl}/business` },
					{ name: "Trip Building", url: `${baseUrl}/business/trip-building` },
				]),
			],
		}),
	},
	{
		path: "/business/faq",
		hostFlavors: ["b2c"],
		page: "BusinessFaqPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "FAQ — Stippl for Business",
			description: "Everything you need to know about Stippl for Business — the visual, drag-and-drop itinerary builder built for travel agents.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				faqPageLd([
					{
						question: "What is Stippl for Business?",
						answer:
							"Stippl for Business is a visual drag-and-drop trip-planning platform built for travel agents. Build branded itineraries in minutes, collaborate in real time, and share interactive trip plans with clients.",
					},
					{
						question: "Can I use my own branding?",
						answer: "Yes. Every itinerary can be branded with your agency's logo, colours, and custom domain. Clients see a fully white-labelled experience.",
					},
					{
						question: "Do you offer a free trial?",
						answer: "Yes. You can start a free trial with no credit card required. After the trial you can pick a plan that matches your team size.",
					},
					{
						question: "Is onboarding included?",
						answer: "Yes. Every plan includes onboarding support, we'll help you migrate existing itineraries and set up your branded workspace.",
					},
				]),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Business", url: `${baseUrl}/business` }, { name: "FAQ", url: `${baseUrl}/business/faq` }]),
			],
		}),
	},

	// ------------------------------------------------------------------
	// B2B-host equivalents (business.stippl.io, b2b.stippl.io, feat-b2b*)
	// Same components, paths drop the /business prefix
	// ------------------------------------------------------------------
	{
		path: "/",
		hostFlavors: ["b2b"],
		page: "BusinessHomePage" as const,
		buildHead: ({ baseUrl }) => ({
			title: "Stippl for Business — Itinerary Software for Travel Agents",
			description:
				"Build beautiful, branded itineraries in minutes. The only drag-and-drop trip planner for travel agents, AI-assisted and fully collaborative.",
			canonical: baseUrl,
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [...softwareAppCard(baseUrl, "Stippl for Business", "Visual drag-and-drop trip-planning platform for travel agents and agencies.")],
		}),
	},
	{
		path: "/product/trip-building",
		hostFlavors: ["b2b"],
		page: "BusinessTripBuildingPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for Business: Drag-and-drop trip builder for travel agents",
			description:
				"Build full itineraries visually: drag and drop days, activities, accommodation, and transport. Real-time collaboration, AI assistance, and branded output.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				...softwareAppCard(baseUrl, "Stippl Trip Builder", "Visual drag-and-drop itinerary builder with real-time collaboration and AI assistance."),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Trip Building", url: `${baseUrl}/product/trip-building` }]),
			],
		}),
	},
	{
		path: "/faq",
		hostFlavors: ["b2b"],
		page: "BusinessFaqPage" as const,
		buildHead: ({ baseUrl, pathname }) => ({
			title: "Stippl for Business FAQ: All Your Questions Answered",
			description: "Everything you need to know about Stippl for Business: features, pricing, white-label branding, free trial, and onboarding.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			jsonLd: [
				faqPageLd([
					{
						question: "What is Stippl for Business?",
						answer:
							"Stippl for Business is a visual drag-and-drop trip-planning platform built for travel agents. Build branded itineraries in minutes, collaborate in real time, and share interactive trip plans with clients.",
					},
					{
						question: "Can I use my own branding?",
						answer: "Yes. Every itinerary can be branded with your agency's logo, colours, and custom domain. Clients see a fully white-labelled experience.",
					},
					{
						question: "Do you offer a free trial?",
						answer: "Yes. You can start a free trial with no credit card required. After the trial you can pick a plan that matches your team size.",
					},
					{
						question: "Is onboarding included?",
						answer: "Yes. Every plan includes onboarding support, we'll help you migrate existing itineraries and set up your branded workspace.",
					},
				]),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "FAQ", url: `${baseUrl}/faq` }]),
			],
		}),
	},

	// ------------------------------------------------------------------
	// Blog (B2C only): both list and article use Strapi data loaders
	// ------------------------------------------------------------------
	{
		path: "/blog",
		hostFlavors: ["b2c"],
		page: "Blog" as const,
		hasLoader: true,
		loaderKey: "blogList",
		buildHead: ({ baseUrl, pathname, availableLocales }) => ({
			title: "Stippl Blog — Travel Tips, Destination Guides & Ideas",
			description: "Destination guides, budget travel tips, itinerary inspiration, and packing hacks — written for travelers who plan smarter. Updated weekly.",
			canonical: defaultCanonical(baseUrl, pathname),
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			// Reciprocity, see the note on /blog/:article. The six localised list routes point
			// here; without this pointing back, the cluster is ignored.
			hreflang: buildBlogListHreflang(baseUrl, availableLocales || ["en", "nl", "de", "fr", "es", "it", "pt"]),
			jsonLd: [
				blogLd({
					baseUrl,
					description: "Travel planning inspiration, tips, and stories from the Stippl team — destination guides, packing hacks, itinerary inspiration.",
				}),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}/blog` }]),
				faqPageLd(
					[
						{
							question: "What travel planning tips can I find on the Stippl blog?",
							answer:
								"The Stippl blog covers destination guides, budget travel advice, packing tips, transport options, and day-by-day itinerary inspiration for destinations across Europe, Asia, the Americas, and beyond: written to help you plan smarter, whether you're a first-timer or a frequent traveler.",
						},
						{
							question: "How do I plan a trip on a budget?",
							answer:
								"Start by setting a daily budget before you book anything. Use a free travel budget planner to estimate costs by category: accommodation, food, transport, and activities. Traveling in shoulder season, eating like a local, and using public transport are reliable ways to stretch your money further.",
						},
						{
							question: "What is the best app for planning a travel itinerary?",
							answer:
								"Stippl is a free travel planner that lets you build a day-by-day itinerary, collaborate with co-travelers, track your budget, and manage a packing list: all in one place. Over 1.5 million travelers use it to organise trips on both mobile and desktop.",
						},
						{
							question: "How far in advance should I plan a trip?",
							answer:
								"For popular destinations in peak season, booking accommodation and flights 3–6 months ahead gets you the best prices and availability. For flexible or off-season travel, 4–8 weeks is usually enough. Building your itinerary early: even before you book, helps you spot gaps and make smarter decisions.",
						},
						{
							question: "Which travel destinations are trending right now?",
							answer:
								"Southeast Asia (especially Japan, Vietnam, and Thailand), Southern Europe (Italy, Croatia, Portugal), and emerging destinations like Georgia and Colombia are consistently popular among Stippl users. Check the blog's Destination Guides section for in-depth itineraries and local tips.",
						},
					],
					"Stippl Blog — Travel Planning FAQ"
				),
			],
		}),
	},
	{
		path: "/blog/:article",
		hostFlavors: ["b2c"],
		// Knowledge-base and legacy Strapi articles share this URL space during the
		// migration, so the renderer is picked at request time from the loader's `source`.
		page: "BlogArticleRoute" as const,
		hasLoader: true,
		loaderKey: "blogArticle",
		requiresData: true,
		// See `blogArticleHead` above — this is one of seven call sites (English plus the six
		// locales below), differing only in `lang`.
		buildHead: ({ baseUrl, pathname, data, lang }) => blogArticleHead({ baseUrl, pathname, data, lang: lang || "en" }),
	},

	// ------------------------------------------------------------------
	// Localised blog routes, nl, de, fr, es, it, pt
	// ------------------------------------------------------------------

	// nl blog list
	{
		path: "/nl/blog",
		hostFlavors: ["b2c"],
		page: "Blog" as const,
		hasLoader: true,
		loaderKey: "blogList",
		langOverride: "nl",
		buildHead: ({ baseUrl, availableLocales }) => ({
			title: "Stippl Blog — Reistips, Bestemmingsgidsen & Inspiratie",
			description: "Bestemmingsgidsen, budgetreistips, routeinspiratie en inpakhulp — geschreven voor slimme reizigers. Wekelijks bijgewerkt.",
			canonical: `${baseUrl}/nl/blog`,
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			hreflang: buildBlogListHreflang(baseUrl, availableLocales || ["en", "nl", "de", "fr", "es", "it", "pt"]),
			jsonLd: [
				blogLd({ baseUrl, description: "Reisplanninginspiratie, tips en verhalen van het Stippl-team." }),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}/nl/blog` }]),
			],
		}),
	},
	// nl blog article
	{
		path: "/nl/blog/:article",
		hostFlavors: ["b2c"],
		// Knowledge-base and legacy Strapi articles share this URL space during the migration,
		// same as English — see `blogArticleHead`.
		page: "BlogArticleRoute" as const,
		hasLoader: true,
		loaderKey: "blogArticle",
		requiresData: true,
		langOverride: "nl",
		buildHead: ({ baseUrl, pathname, data, lang }) => blogArticleHead({ baseUrl, pathname, data, lang: lang || "nl" }),
	},

	// de blog list
	{
		path: "/de/blog",
		hostFlavors: ["b2c"],
		page: "Blog" as const,
		hasLoader: true,
		loaderKey: "blogList",
		langOverride: "de",
		buildHead: ({ baseUrl, availableLocales }) => ({
			title: "Stippl Blog: Reisetipps, Reiseführer & Ideen",
			description: "Reiseführer, Budget-Reisetipps, Reiserouten-Inspiration und Packhilfe, für kluge Reisende. Wöchentlich aktualisiert.",
			canonical: `${baseUrl}/de/blog`,
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			hreflang: buildBlogListHreflang(baseUrl, availableLocales || ["en", "nl", "de", "fr", "es", "it", "pt"]),
			jsonLd: [
				blogLd({ baseUrl, description: "Reiseplanungs-Einblicke vom Stippl-Team." }),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}/de/blog` }]),
			],
		}),
	},
	// de blog article
	{
		path: "/de/blog/:article",
		hostFlavors: ["b2c"],
		// Knowledge-base and legacy Strapi articles share this URL space during the migration,
		// same as English — see `blogArticleHead`.
		page: "BlogArticleRoute" as const,
		hasLoader: true,
		loaderKey: "blogArticle",
		requiresData: true,
		langOverride: "de",
		buildHead: ({ baseUrl, pathname, data, lang }) => blogArticleHead({ baseUrl, pathname, data, lang: lang || "de" }),
	},

	// fr blog list
	{
		path: "/fr/blog",
		hostFlavors: ["b2c"],
		page: "Blog" as const,
		hasLoader: true,
		loaderKey: "blogList",
		langOverride: "fr",
		buildHead: ({ baseUrl, availableLocales }) => ({
			title: "Blog Stippl — Conseils Voyage, Guides & Inspiration",
			description: "Guides de destinations, conseils voyage économique, inspiration d'itinéraires — pour les voyageurs malins. Mis à jour chaque semaine.",
			canonical: `${baseUrl}/fr/blog`,
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			hreflang: buildBlogListHreflang(baseUrl, availableLocales || ["en", "nl", "de", "fr", "es", "it", "pt"]),
			jsonLd: [
				blogLd({ baseUrl, description: "Conseils de planification de voyages par l'équipe Stippl." }),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}/fr/blog` }]),
			],
		}),
	},
	// fr blog article
	{
		path: "/fr/blog/:article",
		hostFlavors: ["b2c"],
		// Knowledge-base and legacy Strapi articles share this URL space during the migration,
		// same as English — see `blogArticleHead`.
		page: "BlogArticleRoute" as const,
		hasLoader: true,
		loaderKey: "blogArticle",
		requiresData: true,
		langOverride: "fr",
		buildHead: ({ baseUrl, pathname, data, lang }) => blogArticleHead({ baseUrl, pathname, data, lang: lang || "fr" }),
	},

	// es blog list
	{
		path: "/es/blog",
		hostFlavors: ["b2c"],
		page: "Blog" as const,
		hasLoader: true,
		loaderKey: "blogList",
		langOverride: "es",
		buildHead: ({ baseUrl, availableLocales }) => ({
			title: "Blog Stippl: Consejos de Viaje, Guías y Ideas",
			description: "Guías de destinos, consejos de viaje económico, inspiración de itinerarios, para viajeros inteligentes. Actualizado semanalmente.",
			canonical: `${baseUrl}/es/blog`,
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			hreflang: buildBlogListHreflang(baseUrl, availableLocales || ["en", "nl", "de", "fr", "es", "it", "pt"]),
			jsonLd: [
				blogLd({ baseUrl, description: "Información de planificación de viajes del equipo Stippl." }),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}/es/blog` }]),
			],
		}),
	},
	// es blog article
	{
		path: "/es/blog/:article",
		hostFlavors: ["b2c"],
		// Knowledge-base and legacy Strapi articles share this URL space during the migration,
		// same as English — see `blogArticleHead`.
		page: "BlogArticleRoute" as const,
		hasLoader: true,
		loaderKey: "blogArticle",
		requiresData: true,
		langOverride: "es",
		buildHead: ({ baseUrl, pathname, data, lang }) => blogArticleHead({ baseUrl, pathname, data, lang: lang || "es" }),
	},

	// it blog list
	{
		path: "/it/blog",
		hostFlavors: ["b2c"],
		page: "Blog" as const,
		hasLoader: true,
		loaderKey: "blogList",
		langOverride: "it",
		buildHead: ({ baseUrl, availableLocales }) => ({
			title: "Blog Stippl — Consigli di Viaggio, Guide & Idee",
			description:
				"Guide alle destinazioni, consigli di viaggio economico, ispirazione per itinerari, per viaggiatori intelligenti. Aggiornato settimanalmente.",
			canonical: `${baseUrl}/it/blog`,
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			hreflang: buildBlogListHreflang(baseUrl, availableLocales || ["en", "nl", "de", "fr", "es", "it", "pt"]),
			jsonLd: [
				blogLd({ baseUrl, description: "Approfondimenti sulla pianificazione dei viaggi dal team Stippl." }),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}/it/blog` }]),
			],
		}),
	},
	// it blog article
	{
		path: "/it/blog/:article",
		hostFlavors: ["b2c"],
		// Knowledge-base and legacy Strapi articles share this URL space during the migration,
		// same as English — see `blogArticleHead`.
		page: "BlogArticleRoute" as const,
		hasLoader: true,
		loaderKey: "blogArticle",
		requiresData: true,
		langOverride: "it",
		buildHead: ({ baseUrl, pathname, data, lang }) => blogArticleHead({ baseUrl, pathname, data, lang: lang || "it" }),
	},

	// pt blog list
	{
		path: "/pt/blog",
		hostFlavors: ["b2c"],
		page: "Blog" as const,
		hasLoader: true,
		loaderKey: "blogList",
		langOverride: "pt",
		buildHead: ({ baseUrl, availableLocales }) => ({
			title: "Blog Stippl — Dicas de Viagem, Guias & Inspiração",
			description: "Guias de destinos, dicas de viagem econômica, inspiração para roteiros — para viajantes inteligentes. Atualizado semanalmente.",
			canonical: `${baseUrl}/pt/blog`,
			ogImage: `${baseUrl}${DEFAULT_OG}`,
			hreflang: buildBlogListHreflang(baseUrl, availableLocales || ["en", "nl", "de", "fr", "es", "it", "pt"]),
			jsonLd: [
				blogLd({ baseUrl, description: "Insights de planejamento de viagens da equipe Stippl." }),
				breadcrumbLd([homeBreadcrumb(baseUrl), { name: "Blog", url: `${baseUrl}/pt/blog` }]),
			],
		}),
	},
	// pt blog article
	{
		path: "/pt/blog/:article",
		hostFlavors: ["b2c"],
		// Knowledge-base and legacy Strapi articles share this URL space during the migration,
		// same as English — see `blogArticleHead`.
		page: "BlogArticleRoute" as const,
		hasLoader: true,
		loaderKey: "blogArticle",
		requiresData: true,
		langOverride: "pt",
		buildHead: ({ baseUrl, pathname, data, lang }) => blogArticleHead({ baseUrl, pathname, data, lang: lang || "pt" }),
	},
];

/**
 * Localised variants of the 14 feature pages above, one route per non-English marketing locale —
 * phase 3 of work/locale-coverage-unification.md in stippl-knowledge. Step 1 shipped every
 * (path, locale) pair as `noindex, follow` with the English title/meta; step 2 (here) lets a pair
 * "graduate" to a real, indexable page once it clears two gates, both read from
 * `marketing/content/featurePageLocaleMeta.ts`:
 *
 *   1. `FEATURE_PAGE_LOCALE_META[path][locale]` exists — a hand-written, keyword-researched
 *      title/description for that market, never machine-translated (same rule as `HOME_META`
 *      above).
 *   2. `path` is in `FEATURE_PAGES_WITH_LOCALISED_BODY` — the page's entire visible body, not
 *      just its head, is translated. A page can clear gate 1 without gate 2: e.g. `/esim`'s FAQ
 *      still comes from the English `ESIM_CONTENT.faqItems`, so even a fully keyword-researched
 *      Dutch title for it must not go indexable yet — that would publish a page titled in Dutch
 *      with an English body, which is worse than the `noindex` placeholder it would replace.
 *
 * Three outcomes per (path, locale), in the branch order `buildHead` below actually uses:
 *
 *   - BOTH gates clear → graduated: hand-written title/description, self canonical, default
 *     `index, follow` (no `robots` override), `excludeFromIndex: false`, and a reciprocal
 *     hreflang set built by `buildFeatureHreflang`. Still no `jsonLd` — localised structured data
 *     is a later step of this same phase, not part of graduating the head.
 *   - Gate 1 only (meta exists, body not translated) → title/description from the map, so the
 *     browser tab and social previews read in the visitor's language, but otherwise exactly step
 *     1: `noindex, follow`, `excludeFromIndex: true`, no hreflang.
 *   - Neither gate → exactly step 1, unchanged: English title/description, `noindex, follow`.
 *
 * `metaMap`/`readySet` default to the real exports but are accepted as parameters so tests can
 * inject fixtures without touching this module's own state. Looked up by path against the
 * English routes already defined above rather than hand-written, so `LOCALISED_FEATURE_PATHS`
 * (`i18n/urlLocale.ts`) and this route table cannot silently drift — a path missing its English
 * route throws at module load instead of quietly producing zero locale routes for it.
 *
 * Never emits hreflang to a non-graduated locale: those pages stay `noindex, follow`, and Google
 * treats hreflang pointing at a noindex page as an invalid, self-contradicting annotation.
 */
export function generateLocalisedFeatureRoutes(
	englishRoutes: MarketingRoute[],
	metaMap: FeaturePageLocaleMetaMap = FEATURE_PAGE_LOCALE_META,
	readySet: ReadonlySet<LocalisedFeaturePath> = FEATURE_PAGES_WITH_LOCALISED_BODY
): MarketingRoute[] {
	const nonEnglishLocales = MARKETING_LANG_CODES.filter((locale): locale is NonEnglishLocale => locale !== "en");

	return LOCALISED_FEATURE_PATHS.flatMap((path) => {
		const englishRoute = englishRoutes.find((r) => r.path === path && !r.langOverride && r.hostFlavors.includes("b2c"));
		if (!englishRoute) {
			throw new Error(
				`generateLocalisedFeatureRoutes: no English route found for feature path "${path}" — LOCALISED_FEATURE_PATHS (i18n/urlLocale.ts) and MARKETING_ROUTES have drifted.`
			);
		}
		const bodyReady = readySet.has(path);
		const graduatedLocales = graduatedLocalesForPath(path, metaMap, readySet);

		return nonEnglishLocales.map((locale) => {
			const localeMeta = metaMap[path]?.[locale];
			const graduated = bodyReady && !!localeMeta;

			return {
				path: `/${locale}${path}`,
				hostFlavors: englishRoute.hostFlavors,
				page: englishRoute.page,
				componentProps: englishRoute.componentProps,
				langOverride: locale,
				excludeFromIndex: !graduated,
				buildHead: (params: BuildHeadParams): MarketingHead => {
					const englishHead = englishRoute.buildHead({ ...params, pathname: path, lang: "en" });
					const canonical = langCanonical(params.baseUrl, locale, path);

					if (graduated) {
						return {
							title: localeMeta!.title,
							description: localeMeta!.description,
							ogImage: englishHead.ogImage,
							canonical,
							hreflang: buildFeatureHreflang(params.baseUrl, path, graduatedLocales),
							// No jsonLd yet: localised structured data is a later step of phase 3.
						};
					}

					// Gate 1 only, or neither gate — both stay noindex; the only difference is
					// whether the title/description are this locale's or still the English page's.
					return {
						title: localeMeta ? localeMeta.title : englishHead.title,
						description: localeMeta ? localeMeta.description : englishHead.description,
						ogImage: englishHead.ogImage,
						canonical,
						robots: "noindex, follow",
					};
				},
			};
		});
	});
}

/** The non-English locales that have graduated for `path` — both gates in `generateLocalisedFeatureRoutes`' doc comment. */
function graduatedLocalesForPath(path: LocalisedFeaturePath, metaMap: FeaturePageLocaleMetaMap, readySet: ReadonlySet<LocalisedFeaturePath>): NonEnglishLocale[] {
	if (!readySet.has(path)) return [];
	const localeMeta = metaMap[path];
	if (!localeMeta) return [];
	return (Object.keys(localeMeta) as NonEnglishLocale[]).filter((locale) => !!localeMeta[locale]);
}

/**
 * Gives each graduated feature path's ENGLISH route a matching, reciprocal hreflang tag — the
 * other half of graduation `generateLocalisedFeatureRoutes` doesn't itself touch, since it only
 * builds the non-English routes.
 *
 * A path with zero graduated locales is skipped entirely, so its English route's `buildHead`
 * stays the exact function defined in the `MARKETING_ROUTES` literal above — same object
 * identity, same output — rather than being wrapped and then happening to produce an identical
 * result. That is what keeps the 14 hand-written English route object literals above textually
 * untouched: this function mutates matching route objects in place, after the array literal,
 * instead of editing them.
 */
export function applyFeatureHreflangToEnglishRoutes(
	routes: MarketingRoute[],
	metaMap: FeaturePageLocaleMetaMap = FEATURE_PAGE_LOCALE_META,
	readySet: ReadonlySet<LocalisedFeaturePath> = FEATURE_PAGES_WITH_LOCALISED_BODY
): void {
	for (const path of LOCALISED_FEATURE_PATHS) {
		const graduatedLocales = graduatedLocalesForPath(path, metaMap, readySet);
		if (graduatedLocales.length === 0) continue;

		const englishRoute = routes.find((r) => r.path === path && !r.langOverride && r.hostFlavors.includes("b2c"));
		if (!englishRoute) continue; // generateLocalisedFeatureRoutes already throws on this case

		const originalBuildHead = englishRoute.buildHead;
		englishRoute.buildHead = (params: BuildHeadParams): MarketingHead => ({
			...originalBuildHead(params),
			hreflang: buildFeatureHreflang(params.baseUrl, path, graduatedLocales),
		});
	}
}

// Pushed rather than spread into the `MARKETING_ROUTES` literal above so the 14 hand-written
// English route objects stay textually untouched — see the generator's doc comment. `Array.push`
// on a `const` array mutates in place, which is fine: nothing reads `MARKETING_ROUTES` before
// this line, and `MARKETING_ROUTE_SPECS`/`findMarketingRoute` below both read it after.
MARKETING_ROUTES.push(...generateLocalisedFeatureRoutes(MARKETING_ROUTES));
// Same reasoning: adds hreflang to the English routes that gained a graduated locale sibling
// above, by mutating the route objects already in the array rather than editing their literals.
applyFeatureHreflangToEnglishRoutes(MARKETING_ROUTES);

/**
 * Lightweight subset for backend path matching — no React components,
 * safe to import without pulling the SSR bundle.
 */
export interface MarketingRouteSpec {
	path: string;
	hostFlavors: HostFlavor[];
	hasLoader: boolean;
	loaderKey?: "blogList" | "blogArticle" | "discoverGuide" | "discoverHub" | "readyGeos" | "bioLinks" | "creatorPage" | "partnerMap" | "madMonkeyHostel";
	langOverride?: string;
	requiresData?: boolean;
	/** See MarketingRoute.excludeFromIndex — honoured by server.ts's sitemap and llms.txt builders. */
	excludeFromIndex?: boolean;
	llmsTitle?: string;
	llmsHint?: string;
	/**
	 * Which chunk this route's page lives in — looked up in `marketingPageModuleIds` against the
	 * Vite manifest by `marketingSsrHandler.ts` to emit that chunk's CSS/modulepreload tags. Real
	 * routes always carry one; `DEV_ROUTE_SPECS` (dev has no manifest) does not, hence optional.
	 */
	page?: MarketingPageId;
}

export const MARKETING_ROUTE_SPECS: MarketingRouteSpec[] = MARKETING_ROUTES.map((r) => {
	let llmsTitle: string | undefined;
	let llmsHint: string | undefined;
	if (!r.path.includes(":")) {
		try {
			const head = r.buildHead({ hostFlavor: r.hostFlavors[0], baseUrl: "https://www.stippl.io", pathname: r.path, pathParams: {}, data: null });
			llmsTitle = head.title;
			llmsHint = head.description;
		} catch {
			// leave undefined — buildHead for some routes may need data
		}
	}
	return {
		path: r.path,
		hostFlavors: r.hostFlavors,
		hasLoader: !!r.hasLoader,
		loaderKey: r.loaderKey,
		langOverride: r.langOverride,
		requiresData: !!r.requiresData,
		excludeFromIndex: !!r.excludeFromIndex,
		llmsTitle,
		llmsHint,
		page: r.page,
	};
});

/**
 * Match a request path + host flavor against the manifest.
 * Returns the matched route + extracted path params, or null.
 */
export function findMarketingRoute(pathname: string, hostFlavor: HostFlavor): { route: MarketingRoute; params: Record<string, string> } | null {
	for (const route of MARKETING_ROUTES) {
		if (!route.hostFlavors.includes(hostFlavor)) continue;
		const params = matchRoutePath(route.path, pathname);
		if (params) return { route, params };
	}
	return null;
}

/**
 * Tiny path matcher — supports `/static` and `/path/:param` patterns.
 * Returns extracted params, or null when no match.
 */
export function matchRoutePath(pattern: string, pathname: string): Record<string, string> | null {
	const cleanPattern = pattern.replace(/\/$/, "") || "/";
	const cleanPath = pathname.replace(/\/$/, "") || "/";

	const patternParts = cleanPattern.split("/").filter(Boolean);
	const pathParts = cleanPath.split("/").filter(Boolean);

	if (cleanPattern === "/" && cleanPath === "/") return {};
	if (patternParts.length !== pathParts.length) return null;

	const params: Record<string, string> = {};
	for (let i = 0; i < patternParts.length; i++) {
		const pp = patternParts[i];
		const xp = pathParts[i];
		if (pp.startsWith(":")) {
			params[pp.slice(1)] = decodeURIComponent(xp);
		} else if (pp !== xp) {
			return null;
		}
	}
	return params;
}
